import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signIn, confirmSignIn, getCurrentUser, signOut } from 'aws-amplify/auth';
import { isSandbox } from '../config/environment';
import './AdminLogin.css';

interface SignInState {
  step: 'signIn' | 'newPasswordRequired' | 'authenticated';
  challengeName?: string;
  session?: any;
}

const AdminLoginCustom: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signInState, setSignInState] = useState<SignInState>({ step: 'signIn' });
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkCurrentUser();
  }, []);

  const checkCurrentUser = async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
      setSignInState({ step: 'authenticated' });
    } catch (error) {
      // User not signed in, show login form
      setCurrentUser(null);
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setCurrentUser(null);
      setSignInState({ step: 'signIn' });
      setError(null);
    } catch (error) {
      console.error('Sign out error:', error);
      setCurrentUser(null);
      setSignInState({ step: 'signIn' });
      setError(null);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Use Amplify authentication only
    try {
      // Localhost dev bypass (optional): allow quick login without Cognito when enabled
      if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
          process.env.REACT_APP_DEV_ADMIN_BYPASS === 'true') {
        if (email === 'wilhelm@mintkitchen.de' && password === 'helm') {
          try { window.localStorage.setItem('dev_admin_bypass', '1'); } catch {}
          navigate('/admin/dashboard');
          return;
        }
      }

      const result = await signIn({
        username: email,
        password: password,
      });

      console.log('Sign in result:', result);

      if (result.isSignedIn) {
        // User is fully signed in
        const user = await getCurrentUser();
        console.log('✅ User authenticated:', user.username);
        navigate('/admin/dashboard');
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // User needs to set a new password
        setSignInState({ 
          step: 'newPasswordRequired', 
          challengeName: result.nextStep.signInStep,
          session: result 
        });
      } else {
        setError('Unexpected sign-in state. Please try again.');
      }
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      });

      console.log('Confirm sign in result:', result);

      if (result.isSignedIn) {
        const user = await getCurrentUser();
        console.log('✅ User authenticated with new password:', user.username);
        navigate('/admin/dashboard');
      } else {
        setError('Failed to set new password. Please try again.');
      }
    } catch (err: any) {
      console.error('New password error:', err);
      setError(err.message || 'Failed to set new password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="admin-login-section" data-section="admin-login">
      <div className="container">
        <div className="admin-login-container">
          <div className="admin-login-header">
            <h1>Admin Login</h1>
          </div>

          {checkingAuth && (
            <div className="checking-auth">
              <p>Checking authentication...</p>
            </div>
          )}

          {!checkingAuth && signInState.step === 'authenticated' && (
            <div className="already-signed-in">
              <div className="signed-in-notice">
                <h3>✅ Already Signed In</h3>
                <p>You are currently signed in as: <strong>{currentUser?.username}</strong></p>
                <p>Choose an option below:</p>
              </div>

              <div className="signed-in-actions">
                <button 
                  onClick={() => navigate('/admin/dashboard')}
                  className="admin-login-btn"
                  style={{ marginRight: '10px' }}
                >
                  Go to Dashboard
                </button>
                
                <button 
                  onClick={handleSignOut}
                  className="admin-login-btn secondary"
                >
                  Sign Out & Login Fresh
                </button>
              </div>
            </div>
          )}

          {!checkingAuth && signInState.step === 'signIn' && (
            <form onSubmit={handleSignIn} className="admin-login-form">
              {/* Show sandbox credentials hint - ONLY in sandbox environment */}
              {isSandbox() && (
                <div style={{ 
                  background: '#e3f2fd', 
                  border: '1px solid #2196f3', 
                  borderRadius: '4px', 
                  padding: '12px', 
                  marginBottom: '16px',
                  fontSize: '0.9rem'
                }}>
                  <strong>🏖️ Sandbox Environment</strong><br />
                  Test Credentials:<br />
                  <code>wilhelm@mintkitchen.de</code> / <code>Admin123!</code><br />
                  <em style={{ fontSize: '0.85rem', color: '#666' }}>or</em> <code>admin@test.com</code> / <code>Admin123!</code>
                </div>
              )}

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  placeholder={isSandbox() ? "wilhelm@mintkitchen.de" : "Enter your email"}
                  autoComplete="username"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder={isSandbox() ? "Admin123!" : "Enter your password"}
                  autoComplete="current-password"
                />
              </div>

              <button 
                type="submit" 
                className="admin-login-btn"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}

          {!checkingAuth && signInState.step === 'newPasswordRequired' && (
            <form onSubmit={handleNewPassword} className="admin-login-form">
              <div className="password-change-notice">
                <h3>🔐 Set New Password</h3>
                <p>You must change your temporary password before continuing.</p>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Enter new password (min 8 characters)"
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
              </div>

              <button 
                type="submit" 
                className="admin-login-btn"
                disabled={loading}
              >
                {loading ? 'Setting Password...' : 'Set New Password'}
              </button>
            </form>
          )}


        </div>
      </div>
    </section>
  );
};

export default AdminLoginCustom;
